import { useEffect, useLayoutEffect, useRef, useState } from 'react';

interface DraftViewerProps {
  text: string;
  isStreaming?: boolean;
  editable?: boolean;
  onChange?: (text: string) => void;
}

export function DraftViewer({
  text,
  isStreaming = false,
  editable = true,
  onChange,
}: DraftViewerProps) {
  const [editedText, setEditedText] = useState(text);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const wasStreamingRef = useRef(isStreaming);
  const lastScrollRef = useRef<number | null>(null);

  // Capture the final scroll position just before we swap from streaming to edited text,
  // so we can restore it after the swap (otherwise the textarea resets to top).
  if (wasStreamingRef.current && !isStreaming && textareaRef.current) {
    lastScrollRef.current = textareaRef.current.scrollTop;
  }

  useEffect(() => {
    if (!isStreaming) setEditedText(text);
    wasStreamingRef.current = isStreaming;
  }, [isStreaming, text]);

  // Runs synchronously after DOM mutations but before paint — avoids visible jump.
  // `editedText` is load-bearing: after streaming ends the textarea value switches
  // from `text` → `editedText` (see JSX below), so we re-run to re-anchor scroll.
  // biome-ignore lint/correctness/useExhaustiveDependencies: editedText is read indirectly via the textarea value switch
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;

    // Autosize to content: reset first so scrollHeight reflects a shrink, not just growth.
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;

    if (isStreaming) {
      el.scrollTop = el.scrollHeight;
    } else if (lastScrollRef.current !== null) {
      el.scrollTop = lastScrollRef.current;
      lastScrollRef.current = null;
    }
  }, [isStreaming, text, editedText]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedText(e.target.value);
    onChange?.(e.target.value);
  };

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={isStreaming ? text : editedText}
        onChange={handleChange}
        readOnly={isStreaming || !editable}
        rows={12}
        className={[
          'w-full resize-none overflow-y-auto rounded-md border px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap',
          'min-h-[240px] max-h-[60vh]',
          'text-sf-dark focus:outline-none focus:ring-2 focus:ring-sf-primary',
          isStreaming
            ? 'border-sf-primary/30 bg-sf-primary-light cursor-wait'
            : 'border-sf-primary/20 bg-sf-surface',
          !editable && !isStreaming ? 'cursor-default' : '',
        ].join(' ')}
      />
      {isStreaming && (
        <p className="mt-1 text-xs italic text-sf-primary animate-pulse">
          AIRA is generating the response...
        </p>
      )}
    </div>
  );
}
